import { captureError } from '@/config/sentry';
import { app } from '@/lib/firebase';
import type { Id } from '@/types/api';
import { urlAbsoluteToRelative } from '@/utils/urlAbsoluteToRelative';
import { useQueryClient } from '@tanstack/react-query';
import { getMessaging, isSupported, onMessage } from 'firebase/messaging';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, type To } from 'react-router';
import { GET_NOTIFICATIONS_INFINITE_QUERY_KEY } from '../api/getNotifications';
import { markNotificationAsRead } from '../api/markNotificationAsRead';

export const useInvalidateNotifications = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const processedCacheRef = useRef(false);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [GET_NOTIFICATIONS_INFINITE_QUERY_KEY] });
  }, [queryClient]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    void (async () => {
      if (!(await isSupported())) return;

      try {
        const messaging = getMessaging(app);
        unsubscribe = onMessage(messaging, () => {
          invalidate();
        });
      } catch (error) {
        captureError(error);
      }
    })();

    return () => {
      unsubscribe?.();
    };
  }, [invalidate]);

  useEffect(() => {
    if (processedCacheRef.current) return;

    void (async () => {
      try {
        const cache = await caches.open('notification-nav');
        const response = await cache.match('/notification-data');

        if (response) {
          const data = (await response.json()) as { notificationId?: number; url?: string; timestamp: number };
          await cache.delete('/notification-data');

          if (Date.now() - data.timestamp < 10_000) {
            processedCacheRef.current = true;

            if (data.url) {
              void navigate(urlAbsoluteToRelative(data.url) as To);
            }
            if (data.notificationId) {
              void markNotificationAsRead(data.notificationId).finally(() => invalidate());
            }
          }
        }
      } catch {
        // empty
      }
    })();
  }, [navigate, invalidate]);

  useEffect(() => {
    const onSwMessage = (event: MessageEvent) => {
      const eventData = event.data as { type: string; notificationId: Id; url: string };

      if (eventData.type === 'NOTIFICATION_CLICKED') {
        const id = Number(eventData.notificationId);
        if (eventData.url) {
          const navigateUrl = urlAbsoluteToRelative(eventData.url);
          void navigate(navigateUrl as To);
        }
        void markNotificationAsRead(id).finally(() => invalidate());
      }
    };
    navigator.serviceWorker?.addEventListener('message', onSwMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', onSwMessage);
  }, [invalidate, navigate]);

  useEffect(() => {
    const urlParams = new URLSearchParams(globalThis.location.search);
    const notificationId = urlParams.get('notificationId');
    const notificationUrl = urlParams.get('notificationUrl');

    if (notificationId) {
      const id = Number(notificationId);
      if (notificationUrl) {
        const navigateUrl = urlAbsoluteToRelative(notificationUrl);
        void navigate(navigateUrl as To);
      }
      void markNotificationAsRead(id).finally(() => invalidate());

      const newUrl = new URL(globalThis.location.href);
      newUrl.searchParams.delete('notificationId');
      newUrl.searchParams.delete('notificationUrl');
      globalThis.history.replaceState({}, '', newUrl.toString());
    }
  }, [invalidate, navigate]);
};
